// src/context/AppContext.jsx
import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { shopSettingsApi } from '../api';
import { WS_URL } from '../config/api.config';

const AppContext = createContext();

export const AppProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    const savedLang = localStorage.getItem('language');
    return savedLang || 'zh';
  });

  const [currentUser, setCurrentUserState] = useState(null);
  const [shopSettings, setShopSettingsState] = useState(() => {
    const saved = localStorage.getItem('shop_settings');
    try { return saved ? JSON.parse(saved) : {}; } catch { return {}; }
  });

  // WS
  const wsRef          = useRef(null);
  const wsListenersRef = useRef(new Set());
  const reconnectRef   = useRef(null);
  const wsTokenRef     = useRef(null);

  // 初始化时从 localStorage 加载用户信息
  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        setCurrentUserState(user);
      } catch (err) {
        localStorage.removeItem('user');
      }
    }
  }, []);

  // 登录后加载商家设置
  useEffect(() => {
    if (currentUser) {
      loadShopSettings();
    }
  }, [currentUser?.store_id]);

  const loadShopSettings = async () => {
    try {
      const res = await shopSettingsApi.getSettings();
      if (res.success && res.data) {
        setShopSettingsState(res.data);
        localStorage.setItem('shop_settings', JSON.stringify(res.data));
        if (res.data.shop_name) document.title = res.data.shop_name + ' 收银台';
      }
    } catch (err) {
      console.error('加载商家设置失败:', err);
    }
  };

  // ==================== WebSocket ====================
  // 提示音/通知卡片的展示，统一由 components/common/OrderNotification.jsx 负责
  // （它自己订阅 addWsListener），这里只管连接和消息分发，不重复处理声音

  const connectWs = useCallback((token) => {
    // 已连接则不重复连接
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    if (wsRef.current?.readyState === WebSocket.CONNECTING) return;

    // 浏览器对"脚本自己触发、没有用户主动点击关联"的音频播放限制很严，
    // 之后WebSocket收到消息再调 audio.play() 很容易被拦截（NotAllowedError），
    // 听不到任何声音。这里趁着 connectWs 是紧跟在登录点击这个真实用户交互之后
    // 调用的，把提示音都先播放+立刻暂停一次（用户完全感知不到），
    // "解锁"这几个音频文件后续的自动播放权限——OrderNotification.jsx 那边
    // 用的就是这两个文件（new-order.mp3、payment-success.wav），必须在这里
    // 先解锁一次，不能只在 OrderNotification.jsx 里裸调 play()
    ['/sounds/new-order.mp3', '/sounds/payment-success.wav'].forEach(src => {
      try {
        const primer = new Audio(src);
        primer.volume = 0;
        primer.play().then(() => {
          primer.pause();
          primer.currentTime = 0;
        }).catch(() => {});
      } catch (err) {}
    });

    clearTimeout(reconnectRef.current);
    wsTokenRef.current = token;

    console.log('[WS] 连接:', WS_URL);
    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;

    ws.onopen = () => {
      clearTimeout(reconnectRef.current);
      console.log('[WS] 已连接，发送认证');
      ws.send(JSON.stringify({ type: 'AUTH', token }));
    };

    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type !== 'AUTH_OK') {
          console.log('[WS] 收到消息:', msg.type);
        } else {
          console.log('[WS] 认证成功:', msg.room);
        }
        // 广播给所有监听器
        wsListenersRef.current.forEach(fn => {
          try { fn(msg); } catch {}
        });
      } catch {}
    };

    ws.onclose = () => {
      console.log('[WS] 连接关闭，3秒后重连');
      const t = wsTokenRef.current || localStorage.getItem('token');
      if (t) {
        reconnectRef.current = setTimeout(() => connectWs(t), 3000);
      }
    };

    ws.onerror = () => {
      console.warn('[WS] 连接错误');
    };
  }, []);

  // 登录后自动连接 WS
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (currentUser && token) {
      connectWs(token);
    } else if (!currentUser) {
      // 登出时断开 WS
      clearTimeout(reconnectRef.current);
      wsTokenRef.current = null;
      if (wsRef.current) {
        wsRef.current.onclose = null; // 防止触发重连
        wsRef.current.close();
        wsRef.current = null;
      }
    }
    // 开发环境下React严格模式（StrictMode）会故意把组件挂载过程模拟执行两遍，
    // 专门用来揪出"没写清理函数导致副作用泄漏"这类问题——加上这个清理函数，
    // 避免控制台看到"认证成功"、"收到消息"重复出现两次这种情况
    return () => {
      clearTimeout(reconnectRef.current);
    };
  }, [currentUser, connectWs]);

  // 订阅 WS 消息，返回取消订阅函数
  const addWsListener = useCallback((fn) => {
    wsListenersRef.current.add(fn);
    return () => wsListenersRef.current.delete(fn);
  }, []);

  // ==================== 用户 / 设置 ====================

  const setLanguage = (lang) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
  };

  const setCurrentUser = (user) => {
    setCurrentUserState(user);
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('shop_settings');
      setShopSettingsState({});
    }
  };

  const setShopSettings = (settings) => {
    setShopSettingsState(settings);
    localStorage.setItem('shop_settings', JSON.stringify(settings));
    if (settings?.shop_name) document.title = settings.shop_name + ' 收银台';
  };

  const isHeadquarters = currentUser?.store_type === 'headquarters';

  const getStoreName = (lang = language) => {
    if (!shopSettings) return currentUser?.store_name || '收银台';
    if (lang === 'en') return shopSettings.shop_name_en || shopSettings.shop_name || currentUser?.store_name || '收银台';
    if (lang === 'km') return shopSettings.shop_name_km || shopSettings.shop_name || currentUser?.store_name || '收银台';
    return shopSettings.shop_name || currentUser?.store_name || '收银台';
  };

  const storeName = getStoreName(language);
  const logoUrl   = shopSettings?.logo_url || null;

  const logout = () => setCurrentUser(null);

  const value = {
    language,
    currentUser,
    isHeadquarters,
    shopSettings,
    storeName,
    logoUrl,
    setLanguage,
    setCurrentUser,
    setShopSettings,
    loadShopSettings,
    logout,
    // WS
    addWsListener,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used within AppProvider');
  return context;
};